import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

interface ImportedNode {
  type: string;
  value: string;
  confidence: number;
  hemisphere: string;
  region: string;
  keep: boolean;
}

interface Props {
  replicaId: string;
  onClose: () => void;
  onImportComplete: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const AUDIT_PROMPT = `I want to export my preferences and cognitive traits as structured JSON. Please generate a JSON array where each item has:
- "type": one of "fact", "preference", "behavior", "boundary"
- "value": the specific trait, rule, or preference
- "confidence": a number 0.0–1.0 (how certain you are)
- "hemisphere": one of "left" (identity/skills/governance), "right" (communication/emotion/memory), or "brainstem" (ethics)
- "region": one of "identity", "skills", "governance", "communication", "emotion", "memory", "ethics"

Example:
[
  { "type": "preference", "value": "Prefers concise email responses", "confidence": 0.85, "hemisphere": "right", "region": "communication" },
  { "type": "boundary", "value": "Never share financial data without approval", "confidence": 1.0, "hemisphere": "brainstem", "region": "ethics" }
]

Please generate 10–20 items based on what you know about me.`;

const HEMISPHERE_COLORS: Record<string, string> = {
  left: '#6366f1',
  right: '#22c55e',
  brainstem: '#ef4444',
};

const TYPE_ICONS: Record<string, string> = {
  fact: '📋',
  preference: '💡',
  behavior: '🔄',
  boundary: '🛡️',
};

export const ImportFromAI: React.FC<Props> = ({ replicaId, onClose, onImportComplete }) => {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedNodes, setParsedNodes] = useState<ImportedNode[]>([]);
  const [importing, setImporting] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Step 1: Copy audit prompt ───────────────────────────────
  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(AUDIT_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Step 2: Parse & validate JSON ──────────────────────────
  const handleValidate = useCallback(() => {
    setParseError(null);
    try {
      const parsed = JSON.parse(jsonInput);
      if (!Array.isArray(parsed)) {
        setParseError('Expected a JSON array.');
        return;
      }

      const validHemispheres = ['left', 'right', 'brainstem'];
      const validTypes = ['fact', 'preference', 'behavior', 'boundary', 'evidence'];
      const validRegions = ['identity', 'skills', 'governance', 'communication', 'emotion', 'memory', 'ethics'];

      const nodes: ImportedNode[] = parsed
        .filter((item: any) => item.type && item.value)
        .map((item: any) => ({
          type: validTypes.includes(item.type) ? item.type : 'fact',
          value: String(item.value).slice(0, 500),
          confidence: Math.max(0, Math.min(1, parseFloat(item.confidence) || 0.5)),
          hemisphere: validHemispheres.includes(item.hemisphere) ? item.hemisphere : 'left',
          region: validRegions.includes(item.region) ? item.region : 'identity',
          keep: true,
        }));

      if (nodes.length === 0) {
        setParseError('No valid nodes found. Each item needs at least "type" and "value".');
        return;
      }

      setParsedNodes(nodes);
      setStep(3);
    } catch (e: any) {
      setParseError(`Invalid JSON: ${e.message}`);
    }
  }, [jsonInput]);

  // ── Step 3: Import ─────────────────────────────────────────
  const handleImport = async () => {
    const toImport = parsedNodes.filter(n => n.keep);
    if (toImport.length === 0) return;

    setImporting(true);
    try {
      const authToken = await getToken(user);
      let successCount = 0;

      for (const node of toImport) {
        const res = await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/nodes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            type: node.type,
            value: node.value,
            confidence: node.confidence,
            hemisphere: node.hemisphere,
            region: node.region,
            source: 'imported',
          }),
        });
        if (res.ok) successCount++;
      }

      if (successCount > 0) onImportComplete();
    } catch (e) {
      console.error('Import failed:', e);
    } finally {
      setImporting(false);
    }
  };

  const toggleNode = (i: number) => {
    const updated = [...parsedNodes];
    updated[i].keep = !updated[i].keep;
    setParsedNodes(updated);
  };

  const keptCount = parsedNodes.filter(n => n.keep).length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-600/50 rounded-2xl w-[600px] max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <span className="text-xl">🤖</span>
            <div>
              <h3 className="text-base font-bold text-white">Import from AI</h3>
              <p className="text-xs text-slate-400">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">✕</button>
        </div>

        {/* Progress */}
        <div className="flex px-6 pt-3 gap-1">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${s <= step ? 'bg-indigo-500' : 'bg-slate-700'}`} />
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Audit Prompt */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-bold text-white mb-1">1. Copy the Audit Prompt</h4>
                <p className="text-xs text-slate-400">Send this prompt to any AI assistant (ChatGPT, Claude, etc.) to extract your cognitive traits.</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 max-h-48 overflow-y-auto">
                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">{AUDIT_PROMPT}</pre>
              </div>
              <button onClick={handleCopyPrompt}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl transition-colors">
                {copied ? '✅ Copied!' : '📋 Copy Prompt'}
              </button>
              <button onClick={() => setStep(2)} className="w-full py-2 text-indigo-400 hover:text-indigo-300 text-sm transition-colors">
                I have the JSON response → Next
              </button>
            </div>
          )}

          {/* Step 2: Paste & Validate */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-bold text-white mb-1">2. Paste the AI Response</h4>
                <p className="text-xs text-slate-400">Paste the JSON array generated by the AI assistant.</p>
              </div>
              <textarea
                value={jsonInput}
                onChange={e => { setJsonInput(e.target.value); setParseError(null); }}
                placeholder='[{ "type": "preference", "value": "...", ... }]'
                className="w-full h-48 bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 text-xs text-white font-mono placeholder-slate-600 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
              {parseError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-400">
                  ❌ {parseError}
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                  ← Back
                </button>
                <button onClick={handleValidate} disabled={!jsonInput.trim()}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors">
                  Validate & Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Import */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-white mb-0.5">3. Review & Import</h4>
                  <p className="text-xs text-slate-400">{keptCount} of {parsedNodes.length} nodes selected</p>
                </div>
                <button onClick={() => setParsedNodes(parsedNodes.map(n => ({ ...n, keep: !parsedNodes.every(p => p.keep) })))}
                  className="text-xs text-indigo-400 hover:text-indigo-300">
                  {parsedNodes.every(n => n.keep) ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {parsedNodes.map((node, i) => (
                  <div key={i} onClick={() => toggleNode(i)}
                    className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors border ${
                      node.keep
                        ? 'bg-slate-700/20 border-slate-600/30 hover:bg-slate-700/30'
                        : 'bg-slate-800/20 border-slate-700/20 opacity-50'
                    }`}>
                    <input type="checkbox" checked={node.keep} readOnly className="mt-1 accent-indigo-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm">{TYPE_ICONS[node.type] || '📋'}</span>
                        <span className="text-xs font-medium capitalize" style={{ color: HEMISPHERE_COLORS[node.hemisphere] || '#fff' }}>
                          {node.region}
                        </span>
                        <span className="text-xs text-slate-500">• {Math.round(node.confidence * 100)}%</span>
                      </div>
                      <p className="text-xs text-slate-300 truncate">{node.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                  ← Back
                </button>
                <button onClick={handleImport} disabled={keptCount === 0 || importing}
                  className="flex-1 py-2.5 bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors">
                  {importing ? `Importing ${keptCount} nodes...` : `Import ${keptCount} Nodes`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportFromAI;
